import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { authAxios, QueryStatus } from "../../utils";
import { RootState } from "../../redux/store";
import {
  ActivateOrUpdateLimitBalanceDirectDebitPayload,
  ActivateOrUpdateStatementDirectDebitPayload,
  CompaniesSepaDirectDebitDataApi,
  CompanySepaDirectDebitDataList,
  CreateSepaDirectDebitPayload,
  DeactivateTopUpDirectDebitPayload,
  ResetMemobankMandateBankVerificationProcessPayload,
  SepaDirectDebitHistoryApi,
  UpdateUsageFeesDirectDebitPayload,
  UpdateMemobankMandateStatusPayload,
  UpdateReturnedRefundDatePayload,
} from "./sepaDirectDebitApi";
import { MemobankTransferTriggeredSchema } from "@qivia/schemas-pull-money";

const maxTimePoll = 3000;
const intervalPoll = 1000;

export interface CompaniesSepaDirectDebitState {
  loading: boolean;
  error: string | null;
  companiesSepaDirectDebitDataStatus: QueryStatus;
  companiesSepaDirectDebitData: CompanySepaDirectDebitDataList[];
  createSepaDirectDebitStatus: QueryStatus;
  updateUsageFeesDirectDebitStatus: QueryStatus;
  activateLimitBalanceDirectDebitStatus: QueryStatus;
  updateLimitBalanceDirectDebitStatus: QueryStatus;
  activateStatementDirectDebitStatus: QueryStatus;
  updateStatementDirectDebitStatus: QueryStatus;
  deactivateTopUpDirectDebitStatus: QueryStatus;
  getSepaDirectDebitListStatus: QueryStatus;
  getSepaDirectDebitList: SepaDirectDebitHistoryApi[];
  updateReturnedRefundDateStatus: QueryStatus;
  updateMemobankMandateStatusToBankRequestedStatus: QueryStatus;
  updateMemobankMandateStatusToBankApprovedStatus: QueryStatus;
  updateMemobankMandateStatusToActiveStatus: QueryStatus;
  resetMemobankMandateBankVerificationProcessStatus: QueryStatus;
  triggerMemobankTransferStatus: QueryStatus;
}

const initialState: CompaniesSepaDirectDebitState = {
  loading: false,
  error: null,
  companiesSepaDirectDebitDataStatus: "idle",
  companiesSepaDirectDebitData: [],
  createSepaDirectDebitStatus: "idle",
  updateUsageFeesDirectDebitStatus: "idle",
  activateLimitBalanceDirectDebitStatus: "idle",
  updateLimitBalanceDirectDebitStatus: "idle",
  activateStatementDirectDebitStatus: "idle",
  updateStatementDirectDebitStatus: "idle",
  deactivateTopUpDirectDebitStatus: "idle",
  getSepaDirectDebitListStatus: "idle",
  getSepaDirectDebitList: [],
  updateReturnedRefundDateStatus: "idle",
  updateMemobankMandateStatusToBankRequestedStatus: "idle",
  updateMemobankMandateStatusToBankApprovedStatus: "idle",
  updateMemobankMandateStatusToActiveStatus: "idle",
  resetMemobankMandateBankVerificationProcessStatus: "idle",
  triggerMemobankTransferStatus: "idle",
};

export const companiesSepaDirectDebitDataAsync = createAsyncThunk(
  "companiesSepaDirectDebitData/call",
  async () => {
    const axios = authAxios();
    const response = await axios.get<CompaniesSepaDirectDebitDataApi[]>(
      `companies/sepa_direct_debit_data`,
    );
    const companiesSepaDirectDebitData: CompanySepaDirectDebitDataList[] =
      response.data.map((companyData) => {
        const sepaDirectDebitType = companyData.hasStatementsDirectDebit
          ? "STATEMENT"
          : companyData.hasLimitBalanceDirectDebit
            ? "LIMIT_BALANCE"
            : "DEACTIVATED";

        return {
          ...companyData,
          sepaDirectDebitType,
          lowerLimit: companyData.lowerLimit
            ? companyData.lowerLimit / 100
            : null,
          upperLimit: companyData.upperLimit
            ? companyData.upperLimit / 100
            : null,
        };
      });

    return companiesSepaDirectDebitData.sort((a, b) =>
      a.companyName.localeCompare(b.companyName),
    );
  },
);

export const createSepaDirectDebitAsync = createAsyncThunk(
  "createSepaDirectDebit/call",
  async (payload: CreateSepaDirectDebitPayload, thunkAPI) => {
    const axios = authAxios();
    await axios.post(`company/create_sepa_direct_debit`, {
      ...payload,
      amount: Math.round(payload.amount * 100),
    });
    // Launch the polling after the creation
    await thunkAPI.dispatch(
      pollSepaDirectDebitStatus({
        companyUuid: payload.companyUuid,
      }),
    );
  },
);

export const updateUsageFeesDirectDebitAsync = createAsyncThunk(
  "updateUsageFeesDirectDebit/call",
  async (payload: UpdateUsageFeesDirectDebitPayload) => {
    const axios = authAxios();
    await axios.post(`company/update_usage_fees_direct_debit`, payload);
  },
);

export const pollSepaDirectDebitStatus = createAsyncThunk(
  "pollSepaDirectDebitStatus/call",
  async (payload: { companyUuid: string }, { dispatch }) => {
    const startTime = Date.now();

    const poll = async (): Promise<void> => {
      try {
        const response = await dispatch(companiesSepaDirectDebitDataAsync());
        const companiesData =
          response.payload as CompanySepaDirectDebitDataList[];

        const companyData = companiesData.find(
          (company) => company.companyUuid === payload.companyUuid,
        );

        if (companyData?.sepaDirectDebitTestStatus === "PENDING") {
          console.log("✅ SEPA Direct Debit status is now PENDING");
          return;
        }

        if (Date.now() - startTime > maxTimePoll) {
          throw new Error(
            "Timeout reached while polling SEPA Direct Debit status",
          );
        }

        await new Promise((resolve) => setTimeout(resolve, intervalPoll));
        return poll();
      } catch (err: unknown) {
        throw new Error(err as string);
      }
    };
    return poll();
  },
);

export const activateLimitBalanceDirectDebitAsync = createAsyncThunk(
  "activateLimitBalanceDirectDebit/call",
  async (payload: ActivateOrUpdateLimitBalanceDirectDebitPayload) => {
    const axios = authAxios();
    await axios.post(`company/activate_limit_balance_direct_debit`, {
      ...payload,
      lowerLimit: payload.lowerLimit * 100,
      upperLimit: payload.upperLimit * 100,
    });
  },
);

export const updateLimitBalanceDirectDebitAsync = createAsyncThunk(
  "updateLimitBalanceDirectDebit/call",
  async (payload: ActivateOrUpdateLimitBalanceDirectDebitPayload) => {
    const axios = authAxios();
    await axios.post(`company/update_limit_balance_direct_debit`, {
      ...payload,
      lowerLimit: payload.lowerLimit * 100,
      upperLimit: payload.upperLimit * 100,
    });
  },
);

export const updateReturnedRefundDateAsync = createAsyncThunk(
  "updateReturnedRefundDate/call",
  async (payload: UpdateReturnedRefundDatePayload) => {
    const axios = authAxios();
    await axios.post(
      `/sepa_direct_debits/${payload.sepaDirectDebitUuid}/update_returned_refund_date`,
      payload,
    );
  },
);

export const activateStatementDirectDebitAsync = createAsyncThunk(
  "activateStatementDirectDebit/call",
  async (payload: ActivateOrUpdateStatementDirectDebitPayload) => {
    const axios = authAxios();
    await axios.post(`company/activate_statement_direct_debit`, payload);
  },
);

export const updateStatementDirectDebitAsync = createAsyncThunk(
  "updateStatementDirectDebit/call",
  async (payload: ActivateOrUpdateStatementDirectDebitPayload) => {
    const axios = authAxios();
    await axios.post(`company/update_statement_frequency`, payload);
  },
);

export const updateMemobankMandateStatusToBankRequestedAsync = createAsyncThunk(
  "updateMemobankMandateStatusToBankRequested/call",
  async (payload: UpdateMemobankMandateStatusPayload) => {
    const axios = authAxios();
    await axios.post(
      `/memobank_mandates/${payload.mandateUuid}/update_status_to_bank_requested`,
      {
        updatedBy: payload.updatedBy,
      },
    );
  },
);

export const updateMemobankMandateStatusToBankApprovedAsync = createAsyncThunk(
  "updateMemobankMandateStatusToBankApproved/call",
  async (payload: UpdateMemobankMandateStatusPayload) => {
    const axios = authAxios();
    await axios.post(
      `/memobank_mandates/${payload.mandateUuid}/update_status_to_bank_approved`,
      {
        updatedBy: payload.updatedBy,
      },
    );
  },
);

export const updateMemobankMandateStatusToActiveAsync = createAsyncThunk(
  "updateMemobankMandateStatusToActive/call",
  async (payload: UpdateMemobankMandateStatusPayload) => {
    const axios = authAxios();
    await axios.post(
      `/memobank_mandates/${payload.mandateUuid}/update_status_to_active`,
      {
        updatedBy: payload.updatedBy,
      },
    );
  },
);

export const resetMemobankMandateBankVerificationProcessAsync =
  createAsyncThunk(
    "resetMemobankMandateBankVerificationProcess/call",
    async (payload: ResetMemobankMandateBankVerificationProcessPayload) => {
      const axios = authAxios();
      await axios.post(
        `/memobank_mandates/${payload.mandateUuid}/reset_bank_verification_process`,
        {
          updatedBy: payload.updatedBy,
        },
      );
    },
  );

export const deactivateTopUpDirectDebitAsync = createAsyncThunk(
  "deactivateTopUpDirectDebit/call",
  async (payload: DeactivateTopUpDirectDebitPayload) => {
    const axios = authAxios();
    await axios.post(`company/deactivate_top_up_direct_debit`, payload);
  },
);

export const getSepaDirectDebitListAsync = createAsyncThunk(
  "getSepaDirectDebitList/call",
  async () => {
    const axios = authAxios();
    const response =
      await axios.get<SepaDirectDebitHistoryApi[]>(`/sepa_direct_debits`);
    return response.data.map((item) => ({
      ...item,
      amount: item.amount / 100,
    }));
  },
);

export const triggerMemobankTransferAsync = createAsyncThunk(
  "triggerMemobankTransfer/call",
  async (payload: MemobankTransferTriggeredSchema, thunkAPI) => {
    const axios = authAxios();
    await axios.post(`/sepa_direct_debits/trigger`, payload);

    const startTime = Date.now();

    const poll = async (): Promise<void> => {
      const response = await axios.get<SepaDirectDebitHistoryApi[]>(`/sepa_direct_debits`);
      const sepaDirectDebit = response.data.find(
        (item) => item.uuid === payload.memobankSepaDirectDebitUuid,
      );

      if (!sepaDirectDebit) {
        throw new Error(
          "SEPA Direct Debit not found",
        );
      }

      if (sepaDirectDebit.transferStatus === "PENDING") {
        thunkAPI.dispatch(companiesSepaDirectDebitSlice.actions.setSepaDirectDebitList(response.data));
        return;
      }

      if (Date.now() - startTime > maxTimePoll) {
        throw new Error(
          "Timeout reached while polling SEPA Direct Debit transfer status",
        );
      }

      await new Promise((resolve) => setTimeout(resolve, intervalPoll));
      return poll();
    };

    return poll();
  },
);

export const companiesSepaDirectDebitSlice = createSlice({
  name: "companiesSepaDirectDebit",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    resetCreateSepaDirectDebitStatus: (state) => {
      state.createSepaDirectDebitStatus = "idle";
    },
    resetUpdateUsageFeesDirectDebitStatus: (state) => {
      state.updateUsageFeesDirectDebitStatus = "idle";
      state.companiesSepaDirectDebitDataStatus = "idle";
    },
    resetCompaniesSepaDirectDebitDataStatus: (state) => {
      state.companiesSepaDirectDebitDataStatus = "idle";
    },
    resetUpdateLimitBalanceDirectDebitStatus: (state) => {
      state.updateLimitBalanceDirectDebitStatus = "idle";
    },
    resetUpdateStatementDirectDebitStatus: (state) => {
      state.updateStatementDirectDebitStatus = "idle";
    },
    resetDeactivateTopUpDirectDebitStatus: (state) => {
      state.deactivateTopUpDirectDebitStatus = "idle";
    },
    resetActivateStatementDirectDebitStatus: (state) => {
      state.activateStatementDirectDebitStatus = "idle";
    },
    resetActivateLimitBalanceDirectDebitStatus: (state) => {
      state.activateLimitBalanceDirectDebitStatus = "idle";
    },
    resetUpdateReturnedRefundDateStatus: (state) => {
      state.updateReturnedRefundDateStatus = "idle";
    },
    resetTriggerMemobankTransferStatus: (state) => {
      state.triggerMemobankTransferStatus = "idle";
    },
    setSepaDirectDebitList: (state, action: PayloadAction<SepaDirectDebitHistoryApi[]>) => {
      state.getSepaDirectDebitList = action.payload;
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(companiesSepaDirectDebitDataAsync.pending, (state) => {
        state.companiesSepaDirectDebitDataStatus = "processing";
      })
      .addCase(companiesSepaDirectDebitDataAsync.fulfilled, (state, action) => {
        state.companiesSepaDirectDebitData = action.payload;
        state.companiesSepaDirectDebitDataStatus = "success";
      })
      .addCase(companiesSepaDirectDebitDataAsync.rejected, (state) => {
        state.companiesSepaDirectDebitDataStatus = "failed";
      })
      .addCase(createSepaDirectDebitAsync.pending, (state) => {
        state.createSepaDirectDebitStatus = "processing";
      })
      .addCase(createSepaDirectDebitAsync.fulfilled, (state) => {
        state.createSepaDirectDebitStatus = "success";
      })
      .addCase(createSepaDirectDebitAsync.rejected, (state) => {
        state.createSepaDirectDebitStatus = "failed";
      })
      .addCase(updateUsageFeesDirectDebitAsync.pending, (state) => {
        state.updateUsageFeesDirectDebitStatus = "processing";
      })
      .addCase(updateUsageFeesDirectDebitAsync.fulfilled, (state) => {
        state.updateUsageFeesDirectDebitStatus = "success";
      })
      .addCase(updateUsageFeesDirectDebitAsync.rejected, (state) => {
        state.updateUsageFeesDirectDebitStatus = "failed";
      })
      .addCase(activateLimitBalanceDirectDebitAsync.pending, (state) => {
        state.activateLimitBalanceDirectDebitStatus = "processing";
      })
      .addCase(activateLimitBalanceDirectDebitAsync.fulfilled, (state) => {
        state.activateLimitBalanceDirectDebitStatus = "success";
        state.companiesSepaDirectDebitDataStatus = "idle";
      })
      .addCase(activateLimitBalanceDirectDebitAsync.rejected, (state) => {
        state.activateLimitBalanceDirectDebitStatus = "failed";
      })
      .addCase(updateLimitBalanceDirectDebitAsync.pending, (state) => {
        state.updateLimitBalanceDirectDebitStatus = "processing";
      })
      .addCase(updateLimitBalanceDirectDebitAsync.fulfilled, (state) => {
        state.updateLimitBalanceDirectDebitStatus = "success";
        state.companiesSepaDirectDebitDataStatus = "idle";
      })
      .addCase(updateLimitBalanceDirectDebitAsync.rejected, (state) => {
        state.updateLimitBalanceDirectDebitStatus = "failed";
      })
      .addCase(activateStatementDirectDebitAsync.pending, (state) => {
        state.activateStatementDirectDebitStatus = "processing";
      })
      .addCase(activateStatementDirectDebitAsync.fulfilled, (state) => {
        state.activateStatementDirectDebitStatus = "success";
        state.companiesSepaDirectDebitDataStatus = "idle";
      })
      .addCase(activateStatementDirectDebitAsync.rejected, (state) => {
        state.activateStatementDirectDebitStatus = "failed";
      })
      .addCase(updateStatementDirectDebitAsync.pending, (state) => {
        state.updateStatementDirectDebitStatus = "processing";
      })
      .addCase(updateStatementDirectDebitAsync.fulfilled, (state) => {
        state.updateStatementDirectDebitStatus = "success";
        state.companiesSepaDirectDebitDataStatus = "idle";
      })
      .addCase(updateStatementDirectDebitAsync.rejected, (state) => {
        state.updateStatementDirectDebitStatus = "failed";
      })
      .addCase(deactivateTopUpDirectDebitAsync.pending, (state) => {
        state.deactivateTopUpDirectDebitStatus = "processing";
      })
      .addCase(deactivateTopUpDirectDebitAsync.fulfilled, (state) => {
        state.deactivateTopUpDirectDebitStatus = "success";
        state.companiesSepaDirectDebitDataStatus = "idle";
      })
      .addCase(deactivateTopUpDirectDebitAsync.rejected, (state) => {
        state.deactivateTopUpDirectDebitStatus = "failed";
      })
      .addCase(getSepaDirectDebitListAsync.pending, (state) => {
        state.getSepaDirectDebitListStatus = "processing";
      })
      .addCase(getSepaDirectDebitListAsync.fulfilled, (state, action) => {
        state.getSepaDirectDebitListStatus = "success";
        state.getSepaDirectDebitList = action.payload;
      })
      .addCase(getSepaDirectDebitListAsync.rejected, (state) => {
        state.getSepaDirectDebitListStatus = "failed";
      })
      .addCase(updateReturnedRefundDateAsync.pending, (state) => {
        state.updateReturnedRefundDateStatus = "processing";
      })
      .addCase(updateReturnedRefundDateAsync.fulfilled, (state) => {
        state.updateReturnedRefundDateStatus = "success";
        state.getSepaDirectDebitListStatus = "idle";
      })
      .addCase(updateReturnedRefundDateAsync.rejected, (state) => {
        state.updateReturnedRefundDateStatus = "failed";
      })
      .addCase(
        updateMemobankMandateStatusToBankRequestedAsync.pending,
        (state) => {
          state.updateMemobankMandateStatusToBankRequestedStatus = "processing";
        },
      )
      .addCase(
        updateMemobankMandateStatusToBankRequestedAsync.fulfilled,
        (state) => {
          state.updateMemobankMandateStatusToBankRequestedStatus = "success";
          state.companiesSepaDirectDebitDataStatus = "idle";
        },
      )
      .addCase(
        updateMemobankMandateStatusToBankRequestedAsync.rejected,
        (state) => {
          state.updateMemobankMandateStatusToBankRequestedStatus = "failed";
        },
      )
      .addCase(
        updateMemobankMandateStatusToBankApprovedAsync.pending,
        (state) => {
          state.updateMemobankMandateStatusToBankApprovedStatus = "processing";
        },
      )
      .addCase(
        updateMemobankMandateStatusToBankApprovedAsync.fulfilled,
        (state) => {
          state.updateMemobankMandateStatusToBankApprovedStatus = "success";
          state.companiesSepaDirectDebitDataStatus = "idle";
        },
      )
      .addCase(
        updateMemobankMandateStatusToBankApprovedAsync.rejected,
        (state) => {
          state.updateMemobankMandateStatusToBankApprovedStatus = "failed";
        },
      )
      .addCase(updateMemobankMandateStatusToActiveAsync.pending, (state) => {
        state.updateMemobankMandateStatusToActiveStatus = "processing";
      })
      .addCase(updateMemobankMandateStatusToActiveAsync.fulfilled, (state) => {
        state.updateMemobankMandateStatusToActiveStatus = "success";
        state.companiesSepaDirectDebitDataStatus = "idle";
      })
      .addCase(updateMemobankMandateStatusToActiveAsync.rejected, (state) => {
        state.updateMemobankMandateStatusToActiveStatus = "failed";
      })
      .addCase(
        resetMemobankMandateBankVerificationProcessAsync.pending,
        (state) => {
          state.resetMemobankMandateBankVerificationProcessStatus =
            "processing";
        },
      )
      .addCase(
        resetMemobankMandateBankVerificationProcessAsync.fulfilled,
        (state) => {
          state.resetMemobankMandateBankVerificationProcessStatus = "success";
          state.companiesSepaDirectDebitDataStatus = "idle";
        },
      )
      .addCase(
        resetMemobankMandateBankVerificationProcessAsync.rejected,
        (state) => {
          state.resetMemobankMandateBankVerificationProcessStatus = "failed";
        },
      )
      .addCase(pollSepaDirectDebitStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(pollSepaDirectDebitStatus.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(pollSepaDirectDebitStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(triggerMemobankTransferAsync.pending, (state) => {
        state.triggerMemobankTransferStatus = "processing";
      })
      .addCase(triggerMemobankTransferAsync.fulfilled, (state) => {
        state.triggerMemobankTransferStatus = "success";
      })
      .addCase(triggerMemobankTransferAsync.rejected, (state) => {
        state.triggerMemobankTransferStatus = "failed";
      });
  },
});

export const selectCompaniesSepaDirectDebitData = (state: RootState) =>
  state.companiesSepaDirectDebit.companiesSepaDirectDebitData;
export const selectCompaniesSepaDirectDebitDataStatus = (state: RootState) =>
  state.companiesSepaDirectDebit.companiesSepaDirectDebitDataStatus;
export const selectCreateSepaDirectDebitStatus = (state: RootState) =>
  state.companiesSepaDirectDebit.createSepaDirectDebitStatus;
export const selectUpdateUsageFeesDirectDebitStatus = (state: RootState) =>
  state.companiesSepaDirectDebit.updateUsageFeesDirectDebitStatus;
export const selectActivateLimitBalanceDirectDebitStatus = (state: RootState) =>
  state.companiesSepaDirectDebit.activateLimitBalanceDirectDebitStatus;
export const selectUpdateLimitBalanceDirectDebitStatus = (state: RootState) =>
  state.companiesSepaDirectDebit.updateLimitBalanceDirectDebitStatus;
export const selectActivateStatementDirectDebitStatus = (state: RootState) =>
  state.companiesSepaDirectDebit.activateStatementDirectDebitStatus;
export const selectUpdateStatementDirectDebitStatus = (state: RootState) =>
  state.companiesSepaDirectDebit.updateStatementDirectDebitStatus;
export const selectDeactivateTopUpDirectDebitStatus = (state: RootState) =>
  state.companiesSepaDirectDebit.deactivateTopUpDirectDebitStatus;
export const selectGetSepaDirectDebitListStatus = (state: RootState) =>
  state.companiesSepaDirectDebit.getSepaDirectDebitListStatus;
export const selectGetSepaDirectDebitList = (state: RootState) =>
  state.companiesSepaDirectDebit.getSepaDirectDebitList;
export const selectUpdateReturnedRefundDateStatus = (state: RootState) =>
  state.companiesSepaDirectDebit.updateReturnedRefundDateStatus;
export const selectUpdateMemobankMandateStatusToBankRequestedStatus = (
  state: RootState,
) =>
  state.companiesSepaDirectDebit
    .updateMemobankMandateStatusToBankRequestedStatus;
export const selectUpdateMemobankMandateStatusToBankApprovedStatus = (
  state: RootState,
) =>
  state.companiesSepaDirectDebit
    .updateMemobankMandateStatusToBankApprovedStatus;

export const selectUpdateMemobankMandateStatusToActiveStatus = (
  state: RootState,
) => state.companiesSepaDirectDebit.updateMemobankMandateStatusToActiveStatus;

export const selectResetMemobankMandateBankVerificationProcessStatus = (
  state: RootState,
) =>
  state.companiesSepaDirectDebit
    .resetMemobankMandateBankVerificationProcessStatus;

export const selectTriggerMemobankTransferStatus = (state: RootState) =>
  state.companiesSepaDirectDebit.triggerMemobankTransferStatus;

export default companiesSepaDirectDebitSlice.reducer;
